import { Link, useParams } from "react-router-dom";
import EmptyState from "@/components/base/EmptyState";
import MovieCard from "@/components/feature/MovieCard";
import { getMoviesForNews, getNewsById } from "@/utils/media";
import { useSeo } from "@/hooks/useSeo";

export default function NewsDetail() {
  const { id = "" } = useParams();
  const news = getNewsById(id);
  const relatedMovies = news ? getMoviesForNews(news) : [];

  useSeo({
    title: news ? `${news.title} - 91影视资讯` : "影视资讯详情 - 91影视",
    description: news ? news.excerpt : "91影视资讯详情。",
    keywords: news ? `91影视,影视资讯,${news.category}` : "91影视,影视资讯",
  });

  if (!news) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-8">
        <EmptyState
          icon="ri-file-unknow-line"
          title="资讯不存在或已下架"
          description="该资讯可能已被移除，返回资讯列表看看其他内容吧。"
          actionLabel="返回资讯列表"
          to="/news"
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
      <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to="/news" className="transition-colors hover:text-primary-600">
          影视资讯
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="truncate text-foreground-900">{news.title}</span>
      </nav>

      <article className="mt-5 max-w-3xl">
        <div className="flex items-center gap-3 text-xs text-foreground-500">
          <span className="rounded bg-accent-100 px-2 py-0.5 font-medium text-accent-700">{news.category}</span>
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {news.date}
          </span>
        </div>
        <h1 className="mt-3 text-2xl font-extrabold leading-snug text-foreground-950 md:text-3xl">
          {news.title}
        </h1>

        <div className="mt-5 h-56 w-full overflow-hidden rounded-lg bg-background-200 md:h-80">
          <img
            src={news.cover}
            alt={news.title}
            title={`${news.title} - 91影视资讯`}
            className="h-full w-full object-cover object-top"
          />
        </div>

        <div className="mt-6 space-y-4">
          {news.content.map((paragraph) => (
            <p key={paragraph.slice(0, 12)} className="text-sm leading-relaxed text-foreground-700 md:text-base">
              {paragraph}
            </p>
          ))}
        </div>
      </article>

      {relatedMovies.length > 0 && (
        <section className="mt-10">
          <h2 className="mb-5 flex items-center gap-2 text-lg font-bold text-foreground-950">
            <span className="h-5 w-1.5 rounded-full bg-accent-500"></span>
            相关影视作品
          </h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {relatedMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>
      )}

      <div className="mt-10">
        <Link
          to="/news"
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line"></i>
          返回资讯列表
        </Link>
      </div>
    </div>
  );
}